import { LocalStorage } from "enums/localStorage";
import { getPermissions } from "framework/permissions";
import { IUser, IUserColors } from "models/company";
import { IPermission } from "models/permissions";

export function formatDate(dateString: string): string {
  const [year, month, day] = dateString.split('-').map(Number);
  const date = new Date(year, month - 1, day); // Month is zero-based in JavaScript Date
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
}

export function toAmPm(time: string): string {
  return new Date(`1970-01-01T${time}Z`).toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

export function toDDMMYYYY(date: string): string {
  return new Date(date).toLocaleDateString("en-GB"); 
  // en-GB = UK format → dd/mm/yyyy
}

export function formatDateTime(dateTimeString: string): string {
  const date = new Date(dateTimeString);

  if (isNaN(date.getTime())) {
    throw new Error("Invalid date-time string");
  }

  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}


export async function getPermission(
  permissionId: number
): Promise<IPermission | null> {
  // Retrieve permissions object from local storage
  const userId = localStorage.getItem(LocalStorage.ROLE_ID);
  if (!userId) {
    localStorage.clear();
    return null;
  }

  try {
    const permissions: IPermission[] = await getPermissions(`/${userId}`);

    // Check if the permissionId exists in the permissions object
    if (permissions!) {
      return (
        permissions?.find(
          (permission) => permission?.permissionId === permissionId
        ) ?? null
      );
    } else {
      console.error(`Permission with permissionId ${permissionId} not found`);
      return null;
    }
  } catch (error) {
    console.error("Error parsing permissions from local storage:", error);
    return null;
  }
}

export function getUserData() {
  const colors: IUserColors = JSON.parse(
    localStorage.getItem(LocalStorage.COLORS)!
  );
  const user: IUser = JSON.parse(localStorage.getItem(LocalStorage.USER)!);
  const isApprovedVendor: boolean = localStorage.getItem(LocalStorage.IS_APPROVED_VENDOR)! === 'true';
  const roleName: string = localStorage.getItem(LocalStorage.ROLE_NAME)!;
  const companyId: string = localStorage.getItem(LocalStorage.COMPANY_ID)!;

  return { colors, companyId, user, roleName ,isApprovedVendor};
}

export function updateUserData(key: LocalStorage, data: any): void {
  localStorage.setItem(key, JSON.stringify(data));
}

export async function getAllPermission(): Promise<IPermission[] | null> {
  // Retrieve permissions object from local storage
  const userId = localStorage.getItem(LocalStorage.ROLE_ID);
  if (!userId) {
    localStorage.clear();
    return null;
  }

  try {
    const permissions = await getPermissions(`/${userId}`);

    // Check if the permissionId exists in the permissions object
    if (permissions) {
      return permissions;
    } else {
      console.error(`Permission not found`);
      return null;
    }
  } catch (error) {
    console.error("Error parsing permissions from local storage:", error);
    return null;
  }
}

// Function to convert hex to RGB
function hexToRgb(hex: string): [number, number, number] {
  let bigint = parseInt(hex.substring(1), 16);
  let r = (bigint >> 16) & 255;
  let g = (bigint >> 8) & 255;
  let b = bigint & 255;
  return [r, g, b];
}

// Function to convert RGB to HSL
function rgbToHsl(r: number, g: number, b: number): [number, number, number] {
  r /= 255;
  g /= 255;
  b /= 255;
  let max = Math.max(r, g, b);
  let min = Math.min(r, g, b);
  let h: number,
    s: number,
    l: number = (max + min) / 2;

  if (max === min) {
    h = s = 0; // achromatic
  } else {
    let d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
      default:
        h = 0;
    }
    h /= 6;
  }
  return [h, s, l];
}

// Function to convert HSL to RGB
function hslToRgb(h: number, s: number, l: number): [number, number, number] {
  let r: number, g: number, b: number;

  if (s === 0) {
    r = g = b = l; // achromatic
  } else {
    const hue2rgb = (p: number, q: number, t: number): number => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 3) return q;
      if (t < 1 / 2) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };

    let q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    let p = 2 * l - q;
    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }
  return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)];
}

// Function to convert RGB to hex
function rgbToHex(r: number, g: number, b: number): string {
  return (
    "#" +
    ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1).toUpperCase()
  );
}

// Function to generate color palette
export function generatePalette(baseColor: string = "#0000FF"): string[] {
  let [r, g, b] = hexToRgb(baseColor || "#0000FF");
  let [h, s, l] = rgbToHsl(r, g, b);

  let palette: string[] = [];
  for (let i = 0; i < 5; i++) {
    let newLightness = l - i * 0.15 + 0.3; // Adjust the lightness with larger steps
    newLightness = Math.min(1, Math.max(0, newLightness)); // Ensure the lightness is between 0 and 1
    let [newR, newG, newB] = hslToRgb(h, s, newLightness);
    palette.push(rgbToHex(newR, newG, newB));
  }
  return palette;
}

export function isValidEmailRFC5322(email:string): boolean {
  const regex = /^(?:[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}|(?:\[(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?|[a-zA-Z0-9-]*[a-zA-Z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+))\])$/;
  return regex.test(email);
}